/**
 * Retry for server-side calls that never reached the backend.
 *
 * Background (19 AUG 26): a checkout from /payment/purchases hung, nginx cut it
 * with 504 after 60s, and the Next process only saw
 * `connect ETIMEDOUT 104.26.11.41:443` at 72s. The backend was healthy the whole
 * time — two checkouts for the same package succeeded either side of it. The
 * request simply never got a TCP connection: measured from that host, ~30% of
 * fresh connects to the Cloudflare edge lose the first SYN and stall 1.07s, and
 * a long enough run of losses becomes a timeout.
 *
 * WHY ONLY CONNECT ERRORS: retrying a POST that may already have been delivered
 * would mint a second invoice for the same buyer. A connect-phase failure is the
 * one case where we know for certain the backend never saw the request, because
 * the socket was never established. Node prefixes exactly those with "connect ",
 * e.g. "connect ETIMEDOUT ...", "connect ECONNREFUSED ...". Anything after the
 * connection is up — a read timeout, a 5xx, a reset mid-flight — is left alone.
 */

// helpers/axios.js rejects with `error.response` (object) when the server
// answered, or `error.message` (string) when it never did.
export const isUndeliveredRequest = (err) => {
  const message = typeof err === "string" ? err : (err && err.message);

  if (typeof message !== "string") {
    return false;
  }

  return message.indexOf("connect ") === 0;
};

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * @param {Function} fn      performs the request
 * @param {Object}   options retries + backoff
 */
export const retryUndelivered = async (fn, { retries = 2, baseDelayMs = 300 } = {}) => {
  let lastErr;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      lastErr = err;

      if (attempt === retries || !isUndeliveredRequest(err)) {
        throw err;
      }

      // A dropped SYN is retransmitted after ~1s, so a short wait is enough to
      // land on a different attempt rather than the same stalled one.
      // eslint-disable-next-line no-await-in-loop
      await sleep(baseDelayMs * (attempt + 1));
    }
  }

  throw lastErr;
};

const RETRYABLE_READ_STATUSES = [502, 503, 504, 522, 524, 530];

/**
 * GET-only retry classifier. Unlike purchase mutations, an invoice status read
 * is safe to repeat after a timeout because it cannot create or charge anything.
 */
export const isRetryableReadError = (err) => {
  if (typeof err === "string") {
    return true;
  }

  return !!(
    err
    && RETRYABLE_READ_STATUSES.includes(Number(err.status))
  );
};

export const retryIdempotentRead = async (
  fn,
  { retries = 1, baseDelayMs = 200 } = {}
) => {
  let lastErr;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      lastErr = err;

      if (attempt === retries || !isRetryableReadError(err)) {
        throw err;
      }

      // eslint-disable-next-line no-await-in-loop
      await sleep(baseDelayMs * (attempt + 1));
    }
  }

  throw lastErr;
};

export default retryUndelivered;
